import {
    compare,
    createTaskQueue,
    globalRef,
    noop,
    sleep,
    type Task,
    type TaskQueue,
    withTimeout,
} from '@amazon/vinyl-util'

export enum IdlePriority {
    LOW,
    MEDIUM,
    HIGH,
}

/**
 * The number of simultaneous idle tasks to execute.
 */
const SIMULTANEOUS_IDLE_TASKS = 5

/**
 * The number of seconds an idle task may take before it times out.
 */
const IDLE_TASK_TIMEOUT = 60

/**
 * Seconds after bootstrap before idle tasks may begin running.
 */
const STARTUP_IDLE_DELAY = 5

interface IdleQueue {
    readonly tasks: TaskQueue<IdlePriority>
    blocking: number
}

// A global ref rather than module state, so an app reload starts a fresh queue
// and its startup wait runs on the clock of the session that uses it.
const idleQueueRef = globalRef((): IdleQueue => {
    const queue: IdleQueue = {
        tasks: createTaskQueue<IdlePriority>(compare, IdlePriority.MEDIUM),
        blocking: 0,
    }
    // Block from creation so bootstrap enqueues can't outrun the startup wait.
    block(queue, sleep(STARTUP_IDLE_DELAY))
    return queue
})

/**
 * Enqueues a task to be done when the application is idle.
 * @param task
 * @param priority
 */
export function doWhenIdle<T>(
    task: Task<T>,
    priority: IdlePriority = IdlePriority.MEDIUM
): Promise<T> {
    return idleQueueRef.value.tasks.enqueue(
        (abort) => withTimeout(Promise.resolve(task(abort)), IDLE_TASK_TIMEOUT),
        priority
    )
}

function block(queue: IdleQueue, work: Promise<unknown>): void {
    queue.blocking++
    queue.tasks.simultaneous = 0
    work.finally(() => {
        if (!--queue.blocking) {
            queue.tasks.simultaneous = SIMULTANEOUS_IDLE_TASKS
        }
    }).catch(noop)
}

/**
 * Provides a promise that will prevent new idle tasks from executing until the promise settles.
 */
export function blockIdleQueue<T extends Promise<any>>(work: T): T {
    block(idleQueueRef.value, work)
    return work
}
